import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createAction, createFeatureSelector, createReducer, createSelector, on, props } from '@ngrx/store';
import { catchError, map, of, switchMap } from 'rxjs';
import { EstruturaRetornoDadosPaginadosOutputDto } from 'src/app/domain/DTOs/output/estrutura-retorno-dados-paginados.output.dto';
import { MateriaPublicadaHtmlHierarquiaOutputDto } from 'src/app/domain/DTOs/output/publicacao/materia-publicada-html-hierarquia-output.dto';
import { EdicaoPublicadaService } from 'src/app/services/edicao-publicada.service';
import { CommonMessages } from 'src/app/utils/common.messages';
import { ListarMateriasPublicadasHtmlInputDto } from 'src/app/domain/DTOs/input/publicacao/listar-materias-publicadas-html.input.dto';

const actionName = '[Listar Matérias Publicadas HTML]';

export const listarMateriasPublicadasHtml = createAction(`${actionName} Listar`, props<{
  edicaoId: number;
  cadernoEdicaoId?: number;
  paginaAtual: number;
  acumular: boolean;
  entidadeId?: number;
  municipioId?: number;
  secaoId?: number;
  publicacaoId?: number;
}>());
export const listarMateriasPublicadasHtmlSuccess = createAction(`${actionName} Sucesso`, props<{ response: EstruturaRetornoDadosPaginadosOutputDto<MateriaPublicadaHtmlHierarquiaOutputDto[]>; acumular: boolean }>());
export const listarMateriasPublicadasHtmlFailure = createAction(`${actionName} Falha`, props<{ erro: string }>());
export const listarMateriasPublicadasHtmlFinish = createAction(`${actionName} Concluir`);

export interface ListarMateriasPublicadasHtmlState {
  materias: MateriaPublicadaHtmlHierarquiaOutputDto[];
  paginaAtual: number;
  totalDePaginas: number;
  totalDeRegistros: number;
  error: string | null;
  carregando: boolean;
}

const initialState: ListarMateriasPublicadasHtmlState = { materias: [], paginaAtual: 0, totalDePaginas: 0, totalDeRegistros: 0, error: null, carregando: false };

// -- Reducer
export const listarMateriasPublicadasHtmlReducer = createReducer(
  initialState,
  on(listarMateriasPublicadasHtml, (state, { acumular }) => ({ ...state, materias: acumular ? state.materias : [], paginaAtual: acumular ? state.paginaAtual : 0, totalDePaginas: acumular ? state.totalDePaginas : 0, totalDeRegistros: acumular ? state.totalDeRegistros : 0, error: null, carregando: true })),
  on(listarMateriasPublicadasHtmlSuccess, (state, { response, acumular }) => ({ ...state, materias: acumular ? [...state.materias, ...response.dados] : response.dados, paginaAtual: response.paginaAtual, totalDePaginas: response.totalDePaginas, totalDeRegistros: response.totalDeRegistros, error: null, carregando: false })),
  on(listarMateriasPublicadasHtmlFailure, (state, { erro }) => ({ ...state, error: erro, carregando: false })),
  on(listarMateriasPublicadasHtmlFinish, () => initialState)
);

// -- Selector
export const selectListarMateriasPublicadasHtmlState = createFeatureSelector<ListarMateriasPublicadasHtmlState>('listarMateriasPublicadasHtml');
export const selectDadosListarMateriasPublicadasHtml = createSelector(selectListarMateriasPublicadasHtmlState, state => state);

// -- Effect
@Injectable()
export class ListarMateriasPublicadasHtmlEffects {
  listarMateriasPublicadasHtml$ = createEffect(() => this.actions$.pipe(
    ofType(listarMateriasPublicadasHtml),
    switchMap(({ edicaoId, cadernoEdicaoId, paginaAtual, acumular, entidadeId, municipioId, secaoId, publicacaoId }) =>
      this.edicaoPublicadaService.listarMaterias(new ListarMateriasPublicadasHtmlInputDto(
        edicaoId, paginaAtual, cadernoEdicaoId, publicacaoId, entidadeId, municipioId, secaoId
      )).pipe(
        map(response => response.erros?.length
          ? listarMateriasPublicadasHtmlFailure({ erro: response.erros[0] })
          : listarMateriasPublicadasHtmlSuccess({ response, acumular })),
        catchError(() => of(listarMateriasPublicadasHtmlFailure({ erro: CommonMessages.EdicaoHtml.CarregarMaterias })))
      )
    )
  ));

  constructor(private readonly actions$: Actions, private readonly edicaoPublicadaService: EdicaoPublicadaService) {}
}
